import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { useSubscriptionAnalytics } from 'src/hooks/analytics/subscription/useSubscriptionAnalytics.hook';
import { useLoadedFeatureFlag } from 'src/hooks/featureFlags/useLoadedFeatureFlag.hook';
import { SettingsTabs } from '../../pages/Settings/Settings.enum.ts';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider/Subscription.context.ts';
import { useAlertCenterContext } from '../../providers/AlertCenterProvider/AlertCenter.hook.ts';
import { RouterPaths } from '../Router/Router.constants.ts';
import { useProfilingProgressContext } from '../../providers/ProfilingProgressProvider/ProfilingProgress.context';
import NavbarProfilingIndicator from './NavbarProfilingIndicator.component';
import NavbarWorkspaceSelect from './components/NavbarWorkspaceSelect.component';
import LeadFilters from '../LeadFilters/LeadFilters.component';
import AccountHealthBanner from '../AccountHealthBanner/AccountHealthBanner.component';
import AlertBell from '../AlertCenter/AlertBell.component';
import AlertSurfaceBoundary from '../AlertCenter/AlertSurfaceBoundary.component';
import ChallengeCenter from '../ChallengeCenter/ChallengeCenter.component';
import WapiDisconnectedBanner from '../WapiDisconnectedBanner/WapiDisconnectedBanner.component';
import WhatsAppWebBannedBanner from '../WhatsAppWebBannedBanner/WhatsAppWebBannedBanner.component';
import './Navbar.styles.scss';

export default function Navbar() {
  // AGI-427: confirmed non-client only; `!isClient` flashed the trial CTA
  // for paying workspaces during boot.
  const { isSetUpAvailable, isConfirmedNonClient } = useSubscriptionContext();
  // RET-1875 — the Alert Center surfaces mount only when the center delivers
  // (absent from the DOM, never CSS-hidden: KTD14). `areSurfacesEnabled` is
  // the provider's single gating expression: the per-pageload flag snapshot,
  // OR an active dev simulator override (RET-1871, null in deployed builds).
  const { areSurfacesEnabled } = useAlertCenterContext();
  const profilingState = useProfilingProgressContext();
  const isProfilingActive =
    profilingState.active ||
    (profilingState.total > 0 && profilingState.profiled < profilingState.total);
  const location = useLocation();
  const { t } = useTranslation('navbar/navbar');
  const navigate = useNavigate();
  const { trackPurchaseCta } = useSubscriptionAnalytics();

  // The bell's panel state. Owned here (not privately by the bell) since the
  // slot era, and kept here: U3's waiting-criticals section will again need a
  // second way into the SAME panel from outside the bell.
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);

  // Advanced filters are the page-specific half of <LeadFilters />: Directory
  // filters from the navbar, every other route gets search alone. The funnel is
  // no longer an exception here (BUG-815): it draws the filters in its own
  // action bar and lets the navbar keep search, where it lives on every route.
  const isDirectory = location.pathname === RouterPaths.directory;
  // Directory v2 has Filtros in its own table bar, with the count of filters
  // on and a clear button. The navbar still mounts the panel that button
  // opens, and leaves its own Filtros out so the page has one, not two.
  const isDirectoryV2 = useLoadedFeatureFlag('leads_directory-v2');

  // Publish the shell's real height as --LayoutPrivate-nav-height, so pages
  // that fill "everything below the navbar" read it instead of encoding a
  // guess. The critical alert no longer lives in this shell (INT-1653): it is
  // a card in the content column and publishes its own height as
  // --LayoutPrivate-alert-height (see AlertBanner).
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const node = navRef.current;
    if (!node) return undefined;

    const publishHeight = () => {
      document.documentElement.style.setProperty(
        '--LayoutPrivate-nav-height',
        `${node.getBoundingClientRect().height}px`,
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--LayoutPrivate-nav-height');
    };
  }, []);

  return (
    <header ref={navRef} className="NavbarShell">
      <nav className="Navbar">
        <div className="Navbar__leftContainer">
          <img
            className="Navbar__Brand__Icon"
            src="/images/sidebar/brand-mark.svg"
            alt="Leadsales"
          />

          <NavbarWorkspaceSelect />
        </div>

        {/* Account-level warnings (channel disconnected, banned, spam/health)
            live in the shell rather than on each page: they describe the
            workspace, not the view. Mounted once here instead of five times
            across Pipe/Messenger/Dashboard/Directory/WhatsappApi, so they sit
            beside the workspace they refer to and stop pushing page headers
            down. Each self-gates and renders nothing while healthy. This slot
            always occupies the middle so the right cluster never moves. */}
        <div className="Navbar__Banners">
          {/* RET-1879 — legacy retirement: the Alert Center carries these same
              three facts (WhatsApp Web banned, WAPI disconnected, account
              health), so their legacy banners render only while the center is
              off. The slot div itself stays mounted either way — the cluster's
              position must not depend on the flag. */}
          {!areSurfacesEnabled && (
            <>
              <WhatsAppWebBannedBanner />
              <WapiDisconnectedBanner />
              <AccountHealthBanner />
            </>
          )}
        </div>

        {/* Position-stable: pinned to the right regardless of whether a banner
            renders. Navbar__Banners always keeps the middle slot (it no longer
            collapses when empty), so this cluster's position never depends on
            banner presence. */}
        <div className="Navbar__rightContainer">
          {/*
            RET-1871 — Tier 2 of the two-tier containment: each alert-consuming
            surface gets its own boundary so a throw in one cannot take down
            its sibling or the rest of the navbar. Fallback renders nothing —
            an alert surface that "failed" must not itself become an alert.
            RET-1875 — the flag gate sits OUTSIDE the boundary: flag off
            mounts nothing at all (not even the boundary).
          */}
          {areSurfacesEnabled && (
            <AlertSurfaceBoundary surface="bell">
              <AlertBell
                isOpen={isAlertPanelOpen}
                onOpenChange={setIsAlertPanelOpen}
              />
            </AlertSurfaceBoundary>
          )}
          {/* Centro de Retos (AGI-498): the first-week chip lives next to the bell (SAD D16). */}
          <ChallengeCenter />
          <AnimatePresence mode="wait">
            {isProfilingActive ? (
              <NavbarProfilingIndicator key="profiling" state={profilingState} />
            ) : (
              isConfirmedNonClient &&
              isSetUpAvailable &&
              location.pathname !== RouterPaths.settings && (
                <Button
                  key="trial"
                  color="primary"
                  startIcon={<span>🚀</span>}
                  className="glow-button hidden sm:flex !important"
                  onClick={() => {
                    navigate(
                      `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=header`,
                    );

                    trackPurchaseCta('button', 'header');
                  }}
                  data-testid="get-full-access-button"
                >
                  <p className="relative z-[1] flex-1 text-left">
                    {t('buySetUpButton')}
                  </p>
                </Button>
              )
            )}
          </AnimatePresence>
          {/* Search is global: every route reaches the advance-search modal
              from here, the funnel included. It briefly moved into the funnel
              header and that made one control live in two places depending on
              the page, while costing that header the 296px it needed to fit on
              one line (BUG-815). The funnel mounts the filters half instead,
              with showSearch={false}, so no route draws two search boxes.

              Gating the whole component on isDirectory (as this did once) took
              search away from every page that is neither Directory nor the
              funnel. */}
          <LeadFilters
            showFilters={isDirectory}
            showFilterButton={!(isDirectory && isDirectoryV2)}
            showDirectoryFunnelFilter={isDirectory && isDirectoryV2}
            showDirectoryTagsMatch={isDirectory && isDirectoryV2}
          />
        </div>
      </nav>

    </header>
  );
}
